import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { CategoryOptionFilter } from 'graphql-ts-types';
import { space, themeGet } from 'styled-system';

import { trackCollapsibleChange } from 'common/tracking/filters';
import { Direction } from '../../enums/Direction';
import { useCollapsibleList } from '../../hooks/collapsible-list';
import { useLocalization } from '../../providers/LocaleProvider';
import type { SpaceProps } from '../../theme/system-types';
import Checkbox from '../Checkbox';
import { Flex } from '../Flex';
import { Radio } from '../Radio';
import { BodyRegularBR1 } from '../Typography';
import CategoryShowMore from './CategoryShowMore';
import FilterOptionValue from './FilterOptionValue';
import useFilterOptions from './useFilterOptions';

export interface FilterOptionsSectionProps extends Pick<
  CategoryOptionFilter,
  'multiselect' | 'showIcon' | 'options'
> {
  filterKey: string;
  onChange: (filterKey: string, selectedOptions: string[]) => void;
  maxItemsVisible?: number;
  hideArticlesCount?: boolean;
  isDrawer?: boolean;
  showSelectAllOption?: boolean;
}

const ICON_SIZE = 20;
const IMAGE_SIZE = 36;

const FilterOptionCheckbox = styled(Checkbox)<SpaceProps>`
  ${space};

  color: ${({ disabled, checked }) =>
    themeGet(
      disabled
        ? 'colors.sys.neutral.border.default'
        : checked
          ? 'colors.sys.primary.background.default'
          : 'colors.sys.neutral.border.strong'
    )};

  &:hover {
    color: ${({ disabled }) =>
      themeGet(
        disabled ? 'colors.sys.neutral.border.default' : 'colors.sys.primary.background.default'
      )};
  }
`;

const filterOptionCheckboxStyles: ComponentProps<typeof FilterOptionCheckbox> = {
  mb: 'sp_0',
  ml: 'sp_0',
  py: 'sp_4',
};

const FilterOptionRadio = styled(Radio)<SpaceProps>`
  ${space};
`;

const filterOptionRadioStyles: Partial<ComponentProps<typeof FilterOptionRadio>> = {
  mb: 'sp_4',
};

const FiltersShowMore = styled(CategoryShowMore)<SpaceProps>`
  ${space};
`;

const filterOptionsContainerStyles: ComponentProps<typeof Flex> = {
  px: 'sp_8',
  py: 'sp_0',
  flexDirection: 'column',
};

const getRatingAriaLabel = (value: string, numberOfReviews: string): string => {
  const starCount = (value.match(/★/g) || []).length;
  const textAfterStars = value.replace(/★/g, '').trim();

  return `${starCount} ${textAfterStars} ${numberOfReviews}`;
};

const FilterOptionsSection = ({
  filterKey,
  multiselect,
  showIcon,
  options,
  onChange,
  maxItemsVisible,
  hideArticlesCount,
  isDrawer,
  showSelectAllOption = true,
}: FilterOptionsSectionProps) => {
  const { r, t } = useLocalization();

  const {
    selectedOptions,
    onMultiSelectOptionChange,
    onSingleSelectOptionChange,
    areAllSelected,
    onSelectAll,
  } = useFilterOptions({ filterKey, options, onChange });

  const [list, toggle, expanded] = useCollapsibleList({
    list: options,
    maxVisible: maxItemsVisible,
    disabled: !maxItemsVisible,
    callback: isExpanded => trackCollapsibleChange(filterKey, isExpanded),
  });

  if (options.length === 0) {
    return null;
  }

  return (
    <Flex {...filterOptionsContainerStyles} gap={showIcon ? 'sp_0' : 'sp_8'}>
      {multiselect && list.length > 1 && showSelectAllOption && (
        <FilterOptionCheckbox
          {...filterOptionCheckboxStyles}
          label={
            <BodyRegularBR1 as="span" color="inherit">
              {t('h24_select_all_filters')}
            </BodyRegularBR1>
          }
          iconSize={ICON_SIZE}
          checked={areAllSelected}
          onChange={onSelectAll}
          direction={Direction.Rtl}
        />
      )}
      {list.map(({ key, value, numberOfArticles, imagePath }) => {
        const isChecked = selectedOptions.includes(key);

        const ariaLabel =
          filterKey === 'average_rating' &&
          getRatingAriaLabel(value, r.t('h24_ratings_amount', { count: numberOfArticles }));

        const label = (
          <BodyRegularBR1 as="span" color="inherit" {...(ariaLabel && { 'aria-label': ariaLabel })}>
            {hideArticlesCount ? (
              value
            ) : (
              <FilterOptionValue
                value={value}
                numberOfArticles={numberOfArticles}
                isChecked={isChecked}
              />
            )}
          </BodyRegularBR1>
        );

        return multiselect ? (
          <FilterOptionCheckbox
            {...filterOptionCheckboxStyles}
            key={`${filterKey}-${key}`}
            iconSize={ICON_SIZE}
            label={label}
            checked={isChecked}
            disabled={numberOfArticles === 0}
            value={key}
            onChange={onMultiSelectOptionChange}
            direction={Direction.Rtl}
            imageUrl={showIcon ? imagePath || ' ' : null}
            imageAlt={value}
            imageSize={IMAGE_SIZE}
            fillImage
            {...(showIcon && { py: 'sp_12' })}
          />
        ) : (
          <FilterOptionRadio
            {...filterOptionRadioStyles}
            direction={Direction.Rtl}
            onChange={onSingleSelectOptionChange}
            defaultChecked={isChecked}
            iconSize={ICON_SIZE}
            label={label}
            value={key}
            key={`${filterKey}-${key}`}
            name={`option-single-select${isDrawer ? '-drawer' : ''}`}
          />
        );
      })}
      {maxItemsVisible && options.length > maxItemsVisible && (
        <FiltersShowMore pb="sp_12" onClick={toggle} expanded={expanded} />
      )}
    </Flex>
  );
};

export default FilterOptionsSection;
